import { colorsPalettes, mixins } from "@similarweb/styles";
import { Box } from "@similarweb/ui-components";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import type { FunctionComponent } from "react";
import styled from "styled-components";
import BoxSubtitle from "pro-features/components/BoxSubtitle/src/BoxSubtitle";
import { StyledHeaderTitle } from "pro-features/pages/conversion/components/benchmarkOvertime/StyledComponents";
import { TitleContainer } from "pro-features/pages/conversion/components/ConversionScatterChart/StyledComponents";
import { PrimaryBoxTitle } from "pro-features/styled components/StyledBoxTitle/src/StyledBoxTitle";
import { FlexColumn, FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { CircularLoader } from "app/components/React/CircularLoader/CircularLoader";
import { i18nFilter } from "../../../filters/i18nFilter";
import { minVisitsAbbrFilter, percentageFilter } from "../../../filters/ngFilters";
import type { IIncomingTrafficPageData } from "./types";
import { SubTitleReferrals } from "./StyledComponents";
import { MMXAlertWithPlainTooltip } from "app/components/MMXAlertWithPlainTooltip";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { swSettings } from "app/common/services/swSettings";

const StyledBox = styled(Box)<{ height: number }>`
    height: ${({ height }) => height}px;
    display: flex;
    flex-direction: column;
`;

const Wrapper = styled.div`
    height: 100%;
    ${mixins.setFont({ $color: colorsPalettes.carbon[500], $weight: 300 })};
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
`;
const Total = styled.div`
    ${mixins.setFont({ $size: 48 })};
    margin-bottom: 28px;
`;

const Percentage = styled.div`
    ${mixins.setFont({ $size: 16 })};
    text-transform: capitalize;
`;

const MMXAlertWrapper = styled.div`
    align-self: center;
    margin-top: 3px;
    margin-left: 2px;
`;

interface IIncomingTrafficTotalTrafficBoxProps {
    webSource: string;
    from: Dayjs;
    to: Dayjs;
    totalTrafficData: { visits: number; percentage: number };
    isLoading?: boolean;
    is28d?: boolean;
}

export const IncomingTrafficTotalTrafficBox: FunctionComponent<IIncomingTrafficTotalTrafficBoxProps> = ({
    webSource,
    from,
    to,
    totalTrafficData,
    isLoading = false,
    is28d = false,
}) => {
    const subTitleFilters = [
        {
            filter: "date",
            value: {
                from: from.valueOf(),
                to: to.valueOf(),
                useRangeDisplay: !is28d && from.format("YYYY-MM") !== to.format("YYYY-MM"),
            },
        },
        {
            filter: "webSource",
            value: webSource,
        },
    ];
    const title = i18nFilter()("analysis.compare.trafficsource.overview.share");
    const tooltip = i18nFilter()("analysis.compare.trafficsource.overview.share.tooltip");
    let trafficText = "";
    switch (webSource) {
        case "Desktop":
            trafficText = "analysis.totalvisits.span";
            break;
        case "MobileWeb":
            trafficText = "analysis.totalvisits.mobile.span";
            break;
        case "Total":
            trafficText = "analysis.totalvisits.span.alltraffic";
            break;
    }
    const newMMXAlgoStartData = swSettings.current.resources.NewAlgoMMX;
    const isShowMMXAlertBell =
        webSource !== devicesTypes.DESKTOP &&
        from.isBefore(dayjs(newMMXAlgoStartData)) &&
        to.isAfter(dayjs(newMMXAlgoStartData));

    return (
        <div className="span4">
            <StyledBox
                width="100%"
                height={340}
            >
                <TitleContainer>
                    <FlexColumn>
                        <StyledHeaderTitle>
                            <FlexRow>
                                <PrimaryBoxTitle
                                    tooltip={tooltip}
                                    customElement={
                                        isShowMMXAlertBell ? (
                                            <MMXAlertWrapper>
                                                <MMXAlertWithPlainTooltip />
                                            </MMXAlertWrapper>
                                        ) : null
                                    }
                                >
                                    {title}
                                </PrimaryBoxTitle>
                            </FlexRow>
                            <SubTitleReferrals>
                                <BoxSubtitle filters={subTitleFilters} />
                            </SubTitleReferrals>
                        </StyledHeaderTitle>
                    </FlexColumn>
                </TitleContainer>
                <Wrapper>
                    {isLoading ? (
                        <CircularLoader options={{ svg: { stroke: colorsPalettes.carbon["200"] } }} />
                    ) : (
                        <>
                            <Total>{minVisitsAbbrFilter()(totalTrafficData.visits)}</Total>
                            <Percentage>
                                {percentageFilter()(totalTrafficData.percentage, 2)}% {i18nFilter()(trafficText)}
                            </Percentage>
                        </>
                    )}
                </Wrapper>
            </StyledBox>
        </div>
    );
};

export const IncomingTrafficTotalTrafficBoxTransformer = (data: IIncomingTrafficPageData, chosenSites) => {
    const { name } = chosenSites.getPrimarySite();
    const entry = data.dictionary[name];
    if (!entry) {
        return {
            totalTrafficData: { visits: 0, percentage: 0 },
        };
    }
    const { SearchTotal, VolumeTotal } = entry;
    return {
        totalTrafficData: {
            visits: SearchTotal,
            percentage: VolumeTotal ? SearchTotal / VolumeTotal : 0,
        },
    };
};
